'use client';

import React from 'react';
import { styled } from '@linaria/react';

import { BREAKPOINTS } from '@/constants';

import Button from '@/components/LegacyButton';
import ClientOnlySnippet from '@/components/CodeSnippet/ClientOnlySnippet';
import type { ClientLanguage } from '@/components/CodeSnippet/CodeSnippet.types';

import Answer from './Answer';
import Evaluation from './Evaluation';

interface Props {
  preamble?: string;
  question?: string;
  answers: Array<string>;
  code?: string;
  codeLang?: ClientLanguage;
  correctAnswerIndex: number;
}

function MultipleChoiceQuestion({
  preamble,
  question,
  answers,
  code,
  codeLang,
  correctAnswerIndex,
}: Props) {
  const [selectedIndex, setSelectedIndex] = React.useState<
    number | null
  >(null);
  const [result, setResult] = React.useState<
    null | 'correct' | 'incorrect'
  >(null);

  return (
    <Wrapper>
      {question && (
        <Question>
          {preamble}
          {preamble && ' '}
          <QuestionText>{question}</QuestionText>
        </Question>
      )}
      {code && (
        <CodeWrapper>
          <ClientOnlySnippet
            includeContentStyles
            code={code}
            lang={codeLang}
            style={{ marginBottom: 0 }}
          />
        </CodeWrapper>
      )}
      <Answers>
        {answers.map((answer, index) => (
          <Answer
            answer={answer}
            answerIndex={index}
            selectedIndex={selectedIndex}
            key={index}
            highlightAsCorrect={
              !!result && index === correctAnswerIndex
            }
            disabled={!!result}
            handleSelect={() => {
              setSelectedIndex((selectedIndex) =>
                index === selectedIndex ? null : index
              );
            }}
          />
        ))}
      </Answers>
      {result === 'incorrect' &&
        typeof selectedIndex === 'number' && (
          <Evaluation
            providedAnswer={answers[selectedIndex]}
            correctAnswer={answers[correctAnswerIndex]}
          />
        )}
      {!result && (
        <ButtonWrapper>
          <Button
            disabled={typeof selectedIndex !== 'number'}
            onClick={() => {
              setResult(
                selectedIndex === correctAnswerIndex
                  ? 'correct'
                  : 'incorrect'
              );
            }}
          >
            {'Submit'}
          </Button>
        </ButtonWrapper>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div``;

const Question = styled.div`
  font-size: 1.125rem;
  margin-bottom: 32px;
`;

const QuestionText = styled.span`
  font-weight: var(--font-weight-medium);
`;

const Answers = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 32px;

  @media ${BREAKPOINTS.smAndSmaller} {
    margin-left: 32px;
    margin-right: 32px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const CodeWrapper = styled.div`
  padding: 0 32px;
  margin-bottom: 32px;
`;

export default MultipleChoiceQuestion;
